"use client";

import { GameCard } from "@/components/feature/game-card";
import { RiveTile } from "@/components/feature/rive-tile";
import type { PageOriginContext } from "@/lib/page-origin-context";
import type { GameSize, GameWithRelations } from "@/types/game";

/** The content of one tile, whichever layout or editor is showing it. */
export type ShowcaseTileContent =
  | { type: "game"; game: GameWithRelations }
  | { type: "image" | "rive"; assetUrl: string };

interface ShowcaseTileProps {
  item: ShowcaseTileContent;
  /** Visual size of a game card's label. Layouts decide this; assets ignore it. */
  size?: "small" | "medium" | "large";
  /** Grid span a game card reports to its thumbnail `sizes`. Assets ignore it. */
  gameSize?: GameSize;
  pageOrigin?: PageOriginContext | null;
  linkPrefetch?: boolean;
  /** Unique per rendered copy so hover preview only targets the tile under the pointer. */
  previewId?: string;
}

/**
 * The one renderer for section and showcase tiles: rows, curved rows, grids, and their
 * admin previews. Game icons are cropped to the rounded card. Images and Rive files are
 * shown whole and unclipped so their art can break the frame, and Rive files always run
 * their state machine so clicks and hovers reach the animation.
 */
export function ShowcaseTile({
  item,
  size = "small",
  gameSize,
  pageOrigin,
  linkPrefetch = false,
  previewId,
}: ShowcaseTileProps) {
  if (item.type === "game") {
    return (
      <GameCard
        game={gameSize === undefined ? item.game : { ...item.game, size: gameSize }}
        size={size}
        pageOrigin={pageOrigin}
        linkPrefetch={linkPrefetch}
        previewIdOverride={previewId}
      />
    );
  }
  if (item.type === "rive") {
    return <RiveTile src={item.assetUrl} className="h-full w-full" interactive />;
  }
  return (
    // User-supplied section art, possibly a blob: preview in the admin editor.
    // eslint-disable-next-line @next/next/no-img-element
    <img src={item.assetUrl} alt="" className="h-full w-full object-contain" loading="lazy" />
  );
}
